import { FEATURE_TEAM } from '@/lib/features';
import type { ApiClient } from '@/lib/api/client';
import { ApiError } from '@/lib/api/errors';
import {
  serverSettings,
  persistSettings,
  settingsCache,
  subscribeSettings,
  type Settings,
} from './cache';

export interface SettingsDocument {
  settings: Partial<Settings>;
  version: number;
  updated_at: string | null;
}
const path = '/v1/me/settings';
export function startSettingsSync(api: ApiClient) {
  const controller = new AbortController();
  const request = { signal: controller.signal };
  let active = true;
  let busy = false;
  let loaded = false;
  let lastRead = -Infinity;
  let timer: ReturnType<typeof setTimeout>;
  const schedule = (delay = 500) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      void refresh(false);
    }, delay);
  };
  const unsubscribe = subscribeSettings(() => {
    if (Object.keys(settingsCache.pending).length) schedule();
  });
  async function flush() {
    for (const key of Object.keys(settingsCache.pending) as (keyof Settings)[]) {
      if (!active) return;
      const pending = settingsCache.pending[key];
      if (!pending) continue;
      try {
        await api.patch<SettingsDocument>(path, { settings: { [key]: pending.value } }, request);
        if (!active) return;
        if (settingsCache.pending[key] === pending) delete settingsCache.pending[key];
        else if (settingsCache.pending[key]) settingsCache.pending[key].base = pending.value;
        persistSettings();
      } catch (err) {
        if (err instanceof ApiError && err.status === 400) {
          console.error(
            `Settings key "${String(key)}" rejected with 400; dropping from pending queue.`,
            err,
          );
          delete settingsCache.pending[key];
          persistSettings();
          continue;
        }
        throw err;
      }
    }
  }
  async function refresh(read: boolean) {
    if (!active || busy) return;
    busy = true;
    try {
      if (read || !loaded) {
        lastRead = Date.now();
        let doc = await api.get<SettingsDocument>(path, request);
        if (!active) return;
        const migrating = doc.version === 0;
        if (migrating) {
          const migration: Partial<Settings> = { ...settingsCache.value };
          if (!FEATURE_TEAM) {
            delete migration.team_enabled;
            delete migration.team_standing_notes;
            for (const key of Object.keys(migration) as (keyof Settings)[])
              if (key.startsWith('notify_')) delete migration[key];
          }
          const queued = { ...settingsCache.pending };
          try {
            doc = await api.patch<SettingsDocument>(
              path,
              { settings: migration, if_version: 0 },
              request,
            );
          } catch (err) {
            if (err instanceof ApiError && err.status === 400) {
              console.error(
                'Settings migration rejected with 400; retrying without hint keys.',
                err,
              );
              delete migration.shelf_rules_hint_seen;
              delete migration.shelf_rules_hint_retired;
              try {
                doc = await api.patch<SettingsDocument>(
                  path,
                  { settings: migration, if_version: 0 },
                  request,
                );
              } catch (retryErr) {
                if (retryErr instanceof ApiError && retryErr.status === 400) {
                  console.error(
                    'Settings migration retry rejected with 400; continuing with server document.',
                    retryErr,
                  );
                  doc = { settings: {}, version: 1, updated_at: new Date().toISOString() };
                } else {
                  throw retryErr;
                }
              }
            } else {
              throw err;
            }
          }
          if (!active) return;
          for (const key of Object.keys(queued) as (keyof Settings)[]) {
            if (!(key in migration)) continue;
            if (settingsCache.pending[key] === queued[key]) delete settingsCache.pending[key];
            else if (settingsCache.pending[key]) settingsCache.pending[key].base = migration[key];
          }
        }
        const serverTime = Date.parse(doc.updated_at ?? '');
        const next = serverSettings(doc.settings);
        // A response can arrive after a local edit; retain newer queued choices.
        for (const key of Object.keys(settingsCache.pending) as (keyof Settings)[]) {
          const pending = settingsCache.pending[key];
          if (!pending) continue;
          const changedOnServer = JSON.stringify(next[key]) !== JSON.stringify(pending.base);
          if (
            !migrating &&
            changedOnServer &&
            serverTime > pending.changedAt &&
            JSON.stringify(next[key]) !== JSON.stringify(pending.value)
          ) {
            delete settingsCache.pending[key];
          } else Object.assign(next, { [key]: pending.value });
        }
        settingsCache.value = next;
        loaded = true;
        settingsCache.loaded = true;
        persistSettings();
      }
      await flush();
    } catch {
      loaded = false;
      if (active) schedule(30_000);
    } finally {
      busy = false;
    }
  }
  const visible = () => {
    if (document.visibilityState === 'visible' && Date.now() - lastRead >= 60_000)
      void refresh(true);
  };
  const online = () => {
    void refresh(true);
  };
  document.addEventListener('visibilitychange', visible);
  window.addEventListener('online', online);
  void refresh(true);
  return () => {
    active = false;
    controller.abort();
    clearTimeout(timer);
    unsubscribe();
    document.removeEventListener('visibilitychange', visible);
    window.removeEventListener('online', online);
  };
}
